import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { TierProgramRow, TierProgramUpdateInput } from "@colab/shared";

export type UpdateProgramInput = {
  id: string;
  patch: TierProgramUpdateInput;
};

type UpdateProgramResponse = { ok: true; program: TierProgramRow };

// PATCH /api/admin/tiers/programs/:id
//
// Pessimistic. The patch can include `is_default` or `is_archived`; flipping
// either has ripple effects on partner-resolved-tier views and so triggers
// `partners.all` invalidation as well.
//
// The dedicated POST /set-default endpoint is preferred for a single-click
// default toggle UI; this PATCH stays as the catch-all editor save.
export function useUpdateTierProgram() {
  const queryClient = useQueryClient();
  return useMutation<UpdateProgramResponse, ApiClientError, UpdateProgramInput>({
    mutationFn: ({ id, patch }) =>
      apiFetch<UpdateProgramResponse>(`/admin/tiers/programs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, { id, patch }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.programs.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(id) });
      // Default-flip or name-change ripples to every partner-card tier
      // badge (the resolved tier label on unassigned partners depends on
      // which program is the default).
      if (patch.is_default !== undefined || patch.name !== undefined) {
        queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update tier program");
    },
  });
}
